/**
 * The dialogs and the Event Outreach pipeline list of Admin → System → Pipelines (#885), split out of
 * `PipelineDefinitionsTab.jsx`, which keeps the editor, the publish flow and the two 409 confirmations.
 * Everything here is presentational plus its own local state: the Tab owns every request and passes the
 * handlers in.
 */
import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import useDisplayZone from '../../hooks/useDisplayZone';
import { errorText } from '../../lib/apiErrors';
import { plural } from '../../lib/plural';
import { personLabel } from '../../lib/teammateLabels';
import { formatInZone } from '../../lib/timezone';

// The fixed name of each pipeline kind (#986). The deal and lead pipelines always show this, never their stored
// `name`; Event Outreach pipelines show their own, editable name.
export const PIPELINE_LABEL = { deal: 'Deal Pipeline', contact: 'Lead Pipeline', event_contact: 'Event Outreach' };
const inputCls = 'w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20';
export const pillCls = 'text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-600';

/** What a version's `source` means to an admin. */
function sourceLabel(source) {
    if (!source) return '';
    if (source === 'baseline') return 'Baseline before the first edit';
    if (source === 'admin') return 'Admin edit';
    if (source === 'lead_settings') return 'Lead Pipeline settings';
    if (source === 'repair') return 'Repaired snapshot';
    if (source.startsWith('restore:v')) return `Restored from ${source.slice('restore:'.length)}`;
    if (source.startsWith('template:')) return 'Applied a template';
    if (source.startsWith('clone:')) return 'Cloned from another pipeline';
    return source;
}

export function EventPipelineList({ pipelines, canCreate, templatesNote, busy, onNew, onEdit, onRename, onClone, onToggleActive, onMakeDefault }) {
    return (
        <div className="space-y-3" data-testid="event-pipelines">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-base font-semibold text-stone-800">Event Outreach pipelines</h3>
                {canCreate
                    ? <Button size="sm" disabled={busy} data-testid="new-from-template" onClick={onNew}>New from template</Button>
                    : templatesNote && <span className="text-xs text-stone-500" data-testid="templates-unavailable">Templates unavailable: {templatesNote}</span>}
            </div>
            {pipelines.length === 0 ? (
                <div className="text-center py-10 text-stone-400" data-testid="event-pipelines-empty">
                    <p className="text-sm">No Event Outreach pipelines yet</p>
                    {canCreate && <p className="text-xs mt-1">Start from a template to create the first one.</p>}
                </div>
            ) : (
                <div className="bg-white rounded-lg border border-stone-200 divide-y divide-stone-100">
                    {pipelines.map((d) => (
                        <div key={d.pipeline_id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-testid={`event-pipeline-${d.pipeline_id}`}>
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="text-sm font-medium text-stone-900">{d.name}</span>
                                <span className={pillCls}>v{d.version}</span>
                                {d.is_default && <span className={pillCls}>Default</span>}
                                <span className={pillCls}>{d.active ? 'Active' : 'Inactive'}</span>
                            </div>
                            <div className="flex flex-wrap items-center gap-1.5">
                                <Button variant="outline" size="sm" disabled={busy} data-testid={`pipeline-edit-${d.pipeline_id}`} onClick={() => onEdit(d)}>Edit</Button>
                                <Button variant="outline" size="sm" disabled={busy} data-testid={`pipeline-rename-${d.pipeline_id}`} onClick={() => onRename(d)}>Rename</Button>
                                <Button variant="outline" size="sm" disabled={busy} data-testid={`pipeline-clone-${d.pipeline_id}`} onClick={() => onClone(d)}>Clone</Button>
                                <Button variant="outline" size="sm" disabled={busy} data-testid={`pipeline-toggle-${d.pipeline_id}`} onClick={() => onToggleActive(d)}>
                                    {d.active ? 'Deactivate' : 'Activate'}
                                </Button>
                                {d.active && !d.is_default && (
                                    <Button variant="outline" size="sm" disabled={busy} data-testid={`pipeline-make-default-${d.pipeline_id}`} onClick={() => onMakeDefault(d)}>
                                        Make default
                                    </Button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/** Newest first. "View" reads one immutable snapshot; "Restore" publishes a copy of it as the next version. */
export function VersionHistoryDialog({ api, definition, dirty, onRestore, onClose }) {
    const { pipeline_id: pipelineId, stored } = definition;
    const zone = useDisplayZone();
    const [rows, setRows] = useState(null);
    const [error, setError] = useState(null);
    const [viewing, setViewing] = useState(null); // { version, snapshot | null }
    const [confirming, setConfirming] = useState(null);

    useEffect(() => {
        if (!stored) { setRows([]); return undefined; }
        let cancelled = false;
        api.versions(pipelineId).then((data) => { if (!cancelled) setRows(data); })
            .catch((err) => { if (!cancelled) setError(errorText(err, 'Failed to load the version history')); });
        return () => { cancelled = true; };
    }, [api, pipelineId, stored]);

    const view = async (version) => {
        if (viewing?.version === version) { setViewing(null); return; }
        setViewing({ version, snapshot: null });
        try {
            const snapshot = await api.version(pipelineId, version);
            setViewing((cur) => (cur?.version === version ? { version, snapshot } : cur));
        } catch (err) {
            setViewing(null);
            setError(errorText(err, 'Failed to load that version'));
        }
    };

    return (
        <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
            <DialogContent className="sm:max-w-2xl" data-testid="version-history-dialog">
                <DialogHeader>
                    <DialogTitle>Version history</DialogTitle>
                    <DialogDescription>
                        Every publish creates a new version, and restoring one publishes a copy of it. Old versions are never changed.
                    </DialogDescription>
                </DialogHeader>
                {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
                {rows === null && !error && <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-stone-400 animate-spin" /></div>}
                {rows && rows.length === 0 && (
                    <p className="text-sm text-stone-500" data-testid="version-history-empty">
                        No versions yet: this organization still runs the built-in stages.
                    </p>
                )}
                {rows && rows.length > 0 && (
                    <>
                        {dirty && <p className="text-xs text-amber-700">Restoring a version replaces your unsaved edits.</p>}
                        <ul className="divide-y divide-stone-100 border border-stone-200 rounded-lg">
                            {rows.map((r) => (
                                <li key={r.version} className="px-4 py-3 text-sm" data-testid={`version-row-${r.version}`}>
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                            <span className="font-medium text-stone-900">v{r.version}</span>
                                            {r.is_active && <span className={pillCls}>Active</span>}
                                            <span className="text-stone-500">{formatInZone(r.created_at, { zone, formatStr: 'MMM d, yyyy' })}</span>
                                            <span className="text-stone-500">· {personLabel(r.created_by, r.created_by_name) || 'System'}</span>
                                            <span className="text-stone-400">· {sourceLabel(r.source)} · {plural(r.stage_count, 'stage')}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <Button variant="outline" size="sm" data-testid={`version-view-${r.version}`} onClick={() => view(r.version)}>
                                                {viewing?.version === r.version ? 'Hide' : 'View'}
                                            </Button>
                                            {!r.is_active && confirming !== r.version && (
                                                <Button variant="outline" size="sm" data-testid={`version-restore-${r.version}`} onClick={() => setConfirming(r.version)}>Restore</Button>
                                            )}
                                        </div>
                                    </div>
                                    {confirming === r.version && (
                                        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-md bg-amber-50 px-3 py-2 text-amber-900">
                                            <span>Restore v{r.version} as a new version? Nothing is overwritten: it becomes v{definition.version + 1}.</span>
                                            <span className="flex gap-1.5">
                                                <Button variant="outline" size="sm" onClick={() => setConfirming(null)}>Cancel</Button>
                                                <Button size="sm" data-testid={`version-restore-confirm-${r.version}`} onClick={() => onRestore(r.version)}>Restore</Button>
                                            </span>
                                        </div>
                                    )}
                                    {viewing?.version === r.version && (
                                        <div className="mt-2" data-testid={`version-stages-${r.version}`}>
                                            {viewing.snapshot ? (
                                                <ul className="flex flex-wrap gap-1.5">
                                                    {[...(viewing.snapshot.stages || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((s) => (
                                                        <li key={s.key} className="inline-flex items-center gap-1.5 rounded-md bg-stone-100 px-2 py-1 text-xs text-stone-700">
                                                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />{s.label}
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : <Loader2 className="w-4 h-4 text-stone-400 animate-spin" />}
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/** Event Outreach: creates a pipeline (asks for its name). Sales and Lead: publishes the template as the next version. */
export function ApplyTemplateDialog({ kind, templates, definition, dirty, busy, onApply, onRename, onDelete, onClose }) {
    const singleton = kind !== 'event_contact';
    const [selectedId, setSelectedId] = useState(null);
    const [name, setName] = useState('');
    const [renamingId, setRenamingId] = useState(null); // an org template being renamed inline
    const [renameValue, setRenameValue] = useState('');
    const [deletingId, setDeletingId] = useState(null); // an org template awaiting delete confirmation
    const selected = templates.find((t) => t.template_id === selectedId) || null;
    const ready = !!selected && (singleton || name.trim().length > 0);

    const startRename = (t) => { setDeletingId(null); setRenamingId(t.template_id); setRenameValue(t.name); };
    const saveRename = async (t) => { if (await onRename(t, renameValue.trim())) setRenamingId(null); };
    const confirmDelete = async (t) => {
        if (await onDelete(t)) {
            setDeletingId(null);
            if (selectedId === t.template_id) setSelectedId(null);
        }
    };

    return (
        <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
            <DialogContent className="sm:max-w-lg" data-testid="apply-template-dialog">
                <DialogHeader>
                    <DialogTitle>{singleton ? 'Apply a template' : 'New from template'}</DialogTitle>
                    <DialogDescription>
                        {singleton
                            ? `Publishes the template as the next version of the ${PIPELINE_LABEL[kind]}. The current version stays in the history.`
                            : 'Creates a new Event Outreach pipeline. Later changes to a built-in template never affect pipelines already created.'}
                    </DialogDescription>
                </DialogHeader>
                {templates.length === 0 && <p className="text-sm text-stone-500">No templates for this pipeline kind.</p>}
                <ul className="space-y-2 max-h-72 overflow-y-auto">
                    {templates.map((t) => (
                        <li key={t.template_id} data-testid={`template-row-${t.template_id}`}>
                            <label className={`flex items-start gap-2 rounded-lg border px-3 py-2 cursor-pointer ${selectedId === t.template_id ? 'border-stone-900' : 'border-stone-200'}`}>
                                <input type="radio" name="pipeline-template" className="mt-1" checked={selectedId === t.template_id}
                                       data-testid={`template-option-${t.template_id}`} onChange={() => setSelectedId(t.template_id)} />
                                <span className="min-w-0">
                                    <span className="flex items-center gap-2 text-sm font-medium text-stone-900">
                                        {t.name}
                                        <span className={pillCls}>{t.is_system ? 'Built-in' : 'Yours'}</span>
                                    </span>
                                    {t.description && <span className="block text-xs text-stone-500">{t.description}</span>}
                                    <span className="block text-xs text-stone-400 truncate">
                                        {[...(t.stages || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((s) => s.label).join(' · ')}
                                    </span>
                                </span>
                            </label>
                            {!t.is_system && (
                                <div className="mt-1 px-1 text-xs">
                                    {renamingId === t.template_id ? (
                                        <div className="flex flex-wrap items-center gap-1.5">
                                            <input className={`${inputCls} !w-auto flex-1 min-w-[10rem] !py-1`} value={renameValue} maxLength={80} aria-label="Template name"
                                                   data-testid={`template-rename-input-${t.template_id}`} onChange={(e) => setRenameValue(e.target.value)}
                                                   onKeyDown={(e) => {
                                                       if (e.key === 'Enter' && !busy && renameValue.trim() && renameValue.trim() !== t.name) { e.preventDefault(); saveRename(t); }
                                                       if (e.key === 'Escape') { e.stopPropagation(); setRenamingId(null); }
                                                   }} />
                                            <Button size="sm" disabled={busy || !renameValue.trim() || renameValue.trim() === t.name}
                                                    data-testid={`template-rename-save-${t.template_id}`} onClick={() => saveRename(t)}>Save</Button>
                                            <Button size="sm" variant="outline" onClick={() => setRenamingId(null)}>Cancel</Button>
                                        </div>
                                    ) : deletingId === t.template_id ? (
                                        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-amber-50 px-3 py-2 text-amber-900">
                                            <span>Delete this template? Pipelines already made from it are not affected.</span>
                                            <span className="flex gap-1.5">
                                                <Button size="sm" variant="outline" onClick={() => setDeletingId(null)}>Cancel</Button>
                                                <Button size="sm" disabled={busy} data-testid={`template-delete-confirm-${t.template_id}`} onClick={() => confirmDelete(t)}>Delete</Button>
                                            </span>
                                        </div>
                                    ) : (
                                        <span className="flex gap-3 text-stone-500">
                                            <button type="button" className="underline underline-offset-2 hover:text-stone-800" disabled={busy}
                                                    aria-label={`Rename ${t.name}`} data-testid={`template-rename-${t.template_id}`} onClick={() => startRename(t)}>Rename</button>
                                            <button type="button" className="underline underline-offset-2 hover:text-stone-800" disabled={busy}
                                                    aria-label={`Delete ${t.name}`} data-testid={`template-delete-${t.template_id}`} onClick={() => { setRenamingId(null); setDeletingId(t.template_id); }}>Delete</button>
                                        </span>
                                    )}
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
                {!singleton && (
                    <div>
                        <label className="block text-xs font-medium text-stone-600 mb-1" htmlFor="new-pipeline-name">Pipeline name</label>
                        <input id="new-pipeline-name" className={inputCls} value={name} maxLength={80} placeholder="e.g. Fall conference outreach"
                               data-testid="new-pipeline-name" onChange={(e) => setName(e.target.value)} />
                    </div>
                )}
                {singleton && dirty && definition && <p className="text-xs text-amber-700">Applying a template replaces your unsaved edits.</p>}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button disabled={!ready || busy} data-testid="apply-template-confirm" onClick={() => onApply(selected, name.trim())}>
                        {singleton ? 'Apply template' : 'Create pipeline'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/** Renames an Event Outreach pipeline. Stages and versions are untouched: a rename is not a publish. */
export function RenamePipelineDialog({ pipeline, busy, onSave, onClose }) {
    const [name, setName] = useState(pipeline.name);
    const canSave = !busy && !!name.trim() && name.trim() !== pipeline.name;
    return (
        <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
            <DialogContent className="sm:max-w-md" data-testid="rename-pipeline-dialog">
                <DialogHeader>
                    <DialogTitle>Rename pipeline</DialogTitle>
                    <DialogDescription>Only the name changes. The stages and version history stay as they are.</DialogDescription>
                </DialogHeader>
                <div>
                    <label className="block text-xs font-medium text-stone-600 mb-1" htmlFor="rename-pipeline-name">Pipeline name</label>
                    <input id="rename-pipeline-name" className={inputCls} value={name} maxLength={80} data-testid="rename-pipeline-name"
                           onChange={(e) => setName(e.target.value)}
                           onKeyDown={(e) => { if (e.key === 'Enter' && canSave) { e.preventDefault(); onSave(name.trim()); } }} />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button disabled={!canSave} data-testid="rename-pipeline-confirm"
                            onClick={() => onSave(name.trim())}>Rename</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export function SaveTemplateDialog({ busy, defaultName, onSave, onClose }) {
    const [name, setName] = useState(`${defaultName} template`.slice(0, 80));
    return (
        <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
            <DialogContent className="sm:max-w-md" data-testid="save-template-dialog">
                <DialogHeader>
                    <DialogTitle>Save as template</DialogTitle>
                    <DialogDescription>Saves the published stages as a template your organization can reuse.</DialogDescription>
                </DialogHeader>
                <div>
                    <label className="block text-xs font-medium text-stone-600 mb-1" htmlFor="template-name">Template name</label>
                    <input id="template-name" className={inputCls} value={name} maxLength={80} data-testid="template-name"
                           onChange={(e) => setName(e.target.value)}
                           onKeyDown={(e) => { if (e.key === 'Enter' && !busy && name.trim()) { e.preventDefault(); onSave(name.trim()); } }} />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button disabled={busy || !name.trim()} data-testid="save-template-confirm" onClick={() => onSave(name.trim())}>Save template</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
